import { decorationsActions } from 'src/game/decoration/decoration.redux';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver from 'src/StateObserver';
import { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import { getTutorialController, TutorialEvent } from '../TutorialController';
import OrdinaryTutorial from './OrdinaryTutorial';
import {
  isDecorationTutorialInProgress,
  isDecorationUnlocked,
} from '../../decoration/helpers';
import { ActionSequence } from 'src/lib/ActionSequence';
import * as Tutorial from 'src/replicant/tutorial';
import { analytics } from '@play-co/gcinstant';

export class DecorationTutorial extends OrdinaryTutorial {
  public readonly step: TutorialStep = 'decoration';
  private startTime: number = StateObserver.now();

  /**
   * Listen tutorial events
   */
  public async handleEvent(event: TutorialEvent): Promise<boolean> {
    if (event === 'gameStarted' && isDecorationTutorialInProgress()) {
      await ActionSequence.start(
        [() => this.handleDecorationUnlocked()],
        'PostLaunch',
      );
      return false;
    }

    // Show tutorial instruction
    if (event === 'levelIncreased' && isDecorationUnlocked()) {
      return this.handleDecorationUnlocked();
    }

    // User opened decoration scene
    if (event === 'decorationInProgress') {
      return this.handleDecorationInProgress();
    }

    // Give tutorial reward
    if (event === 'decorationFinished') {
      return this.handleDecorationFinished(this.startTime);
    }

    return false;
  }

  private async handleDecorationUnlocked() {
    const { decoration } = StateObserver.getState();

    // Ignore of tutorial dialog was shown
    if (decoration.tutorialDialogWasShown) return false;

    analytics.game.tutorial.TutorialStepStart({
      stepIndex: 17,
      stepName: 'decoration',
      tutorialKey: Tutorial.ruleset.version,
    });
    this.startTime = StateObserver.now();

    await openPopupPromise('popupTutorialStep', {
      step: 'decoration',
    });

    // Register fact that user saw decoration screen
    StateObserver.dispatch(decorationsActions.setTutorialDialogWasShown(true));

    return false;
  }

  private handleDecorationInProgress() {
    const { decoration } = StateObserver.getState();

    // Ignore of tutorial dialog was not shown
    if (!decoration.tutorialDialogWasShown) return false;

    // Remember that we opened decoration screen
    StateObserver.dispatch(
      decorationsActions.setTutorialDecorationScreenWasOpened(true),
    );

    return false;
  }

  private async handleDecorationFinished(startTime: number) {
    const { decoration, user } = StateObserver.getState();

    // Skip reward step if user didn't see decoration screen yet
    if (!decoration.tutorialDecorationScreenWasOpened) return false;

    const coinAmount = Tutorial.getters.getTutorialReward(user, 'decoration');
    if (coinAmount > 0) {
      await openPopupPromise('popupTutorialReward', { coinAmount });
    }
    await StateObserver.invoke.completeTutorialStep('decoration');

    analytics.game.tutorial.TutorialStepFinish({
      stepIndex: 17,
      stepName: 'decoration',
      tutorialKey: Tutorial.ruleset.version,
      stepElapsedSeconds: (StateObserver.now() - startTime) / 1000,
    });

    void getTutorialController().handleEvent('decorationRewardClaimed');

    return true;
  }
}
